/**
 * Confirm-readiness — the single pure answer to "what is this order still
 * missing before it can be confirmed?"
 *
 * Historically this presence check (tractor / trailer / trip type, with the
 * bobtail waiver and the per-leg vs. parent-mirror split) was re-implemented
 * three times against three different data shapes:
 *
 *   - `src/pages/OrderDetail.tsx`     — DB rows (`orders` + `legs`)
 *   - `src/lib/order-form-assets.ts`  — `OrderFormData` (Create / Edit dialogs)
 *   - `src/hooks/useOrderMutations.ts`— the write-path pre-flight
 *
 * Each copy drifted independently, which is exactly how "Confirm Order renders
 * enabled but the save throws Tractor Missing" keeps happening. Callers now
 * normalise their own shape into {@link ConfirmReadinessInput} and share this
 * one implementation, so the button and the write path can only ever agree.
 *
 * This module is intentionally free of React, Supabase and async work: callers
 * resolve trailer-type kinds up front and pass a predicate.
 */

/** A thing the order still needs before it can be confirmed. */
export type ConfirmRequirement =
  | "tractor"
  | "trailer"
  | "trailer type"
  | "trip type"
  | "operator";

/** Canonical report order, so tooltips read the same everywhere. */
const REQUIREMENT_ORDER: ConfirmRequirement[] = [
  "tractor",
  "trailer",
  "trailer type",
  "trip type",
  "operator",
];

/** A Manual leg is human-driven, so it cannot run without an operator. */
function requiresOperator(tripType: string | null | undefined): boolean {
  return (tripType ?? "").toString().trim().toLowerCase() === "manual";
}

/**
 * Trip types that create an external dispatch trip, which needs the trailer
 * kind. Manual trips never reach dispatch, so they may confirm without one.
 */
const TRAILER_TYPE_TRIP_TYPES = new Set(["svo", "nvo", "ci"]);
export function requiresTrailerType(tripType: string | null | undefined): boolean {
  return TRAILER_TYPE_TRIP_TYPES.has((tripType ?? "").toString().trim().toLowerCase());
}

export interface ConfirmReadinessLeg {
  /** `legs.assigned_tractor_id`. No parent fallback — legs are authoritative. */
  tractorId?: string | null;
  /** `legs.trailer_id` (trailer *number*, text). */
  trailerId?: string | null;
  /**
   * Effective trailer type for this leg: the leg's own, else the parent
   * mirror. The caller resolves the fallback so this module stays pure.
   */
  trailerTypeId?: string | null;
  /** Effective trip type for this leg: the leg's own, else the parent mirror. */
  tripType?: string | null;
  /** `legs.assigned_operator_id`. Required only when the leg is Manual. */
  operatorId?: string | null;
}


export interface ConfirmReadinessInput {
  /** Order-level tractor. Used only when `legs` is absent/empty. */
  tractorId?: string | null;
  /** Order-level trailer number. Used only when `legs` is absent/empty. */
  trailerId?: string | null;
  /** Order-level trailer type. Used only when `legs` is absent/empty. */
  trailerTypeId?: string | null;
  /** Order-level trip type. Used only when `legs` is absent/empty. */
  tripType?: string | null;
  /** Order-level operator 1. Used only when `legs` is absent/empty. */
  operatorId?: string | null;

  /**
   * Per-leg rows, passed ONLY when legs are the source of truth for assets
   * (multi-leg parents on the detail page, per-leg-asset forms). When present
   * the order-level mirrors are ignored entirely — they are a stale leg-1
   * mirror and must never be used as a fallback for legs 2..N.
   */
  legs?: readonly ConfirmReadinessLeg[];
  /** True for trailer types whose `kind` is `bobtail` (no trailer required). */
  isBobtailTrailerType?: (trailerTypeId: string | null | undefined) => boolean;
  /**
   * Whether to require a trip type. Defaults to `true`. The form dialogs
   * validate trip type separately (`hasConfirmTripType`) and opt out.
   */
  checkTripType?: boolean;
  /**
   * Whether to require a tractor. Defaults to `true`. The write-path pre-flight
   * validates per-leg tractors in its own multi-leg promotion step (with a
   * different error sentinel) and opts out here.
   */
  checkTractor?: boolean;
}

function isSet(value: string | null | undefined): boolean {
  return (value ?? "").toString().trim() !== "";
}

/**
 * Which confirm requirements are still unmet? Empty array ⇒ ready to confirm
 * as far as asset presence goes (documentation gating is separate — see
 * `src/lib/confirm-doc-gating.ts`).
 *
 * Fails closed: a per-leg order with zero legs reports every requirement as
 * missing rather than silently passing.
 */
export function missingConfirmRequirements(
  input: ConfirmReadinessInput,
): ConfirmRequirement[] {
  const checkTripType = input.checkTripType !== false;
  const checkTractor = input.checkTractor !== false;
  const isBobtail = input.isBobtailTrailerType ?? (() => false);
  const missing = new Set<ConfirmRequirement>();

  if (input.legs) {
    if (input.legs.length === 0) {
      // No legs to validate against — nothing proves the order is ready.
      if (checkTractor) missing.add("tractor");
      missing.add("trailer");
      if (checkTripType) missing.add("trip type");
    }
    for (const leg of input.legs) {
      if (checkTractor && !isSet(leg.tractorId)) missing.add("tractor");
      if (!isSet(leg.trailerId) && !isBobtail(leg.trailerTypeId)) missing.add("trailer");
      if (requiresTrailerType(leg.tripType) && !isSet(leg.trailerTypeId)) missing.add("trailer type");
      if (checkTripType && !isSet(leg.tripType)) missing.add("trip type");
      // Manual legs are driven by a person — no operator, no confirmation.
      if (requiresOperator(leg.tripType) && !isSet(leg.operatorId)) {
        missing.add("operator");
      }
    }
  } else {
    if (checkTractor && !isSet(input.tractorId)) missing.add("tractor");
    if (!isSet(input.trailerId) && !isBobtail(input.trailerTypeId)) missing.add("trailer");
    if (requiresTrailerType(input.tripType) && !isSet(input.trailerTypeId)) missing.add("trailer type");
    if (checkTripType && !isSet(input.tripType)) missing.add("trip type");
    if (requiresOperator(input.tripType) && !isSet(input.operatorId)) {
      missing.add("operator");
    }
  }


  return REQUIREMENT_ORDER.filter((r) => missing.has(r));
}

/** Convenience: is the order asset-ready to confirm? */
export function isConfirmReady(input: ConfirmReadinessInput): boolean {
  return missingConfirmRequirements(input).length === 0;
}

/**
 * Builds an `isBobtailTrailerType` predicate from a trailer-types list.
 * Returns `false` for an unknown id — bobtail is a waiver, so an unresolved
 * type must keep the trailer required (fail closed).
 */
export function bobtailPredicate(
  trailerTypes: readonly { id: string; kind?: string | null }[] | undefined,
): (trailerTypeId: string | null | undefined) => boolean {
  const bobtailIds = new Set(
    (trailerTypes ?? []).filter((t) => t.kind === "bobtail").map((t) => t.id),
  );
  return (id) => !!id && bobtailIds.has(id);
}
